/**
 * Page layout for printable worksheets: paper, type size, columns and print CSS.
 */

export type PaperSize = "letter" | "a4";
export type FontSize = "small" | "medium" | "large";
export type ProblemLayout = "vertical" | "horizontal";

/** Problem type size in points. 14 pt is dense, 24 pt suits early writers. */
export const FONT_POINTS: Record<FontSize, number> = {
  small: 14,
  medium: 18,
  large: 24,
};

export const MIN_PROBLEMS = 1;
export const MAX_PROBLEMS = 60;
export const PROBLEM_COUNT_CHOICES = [
  4, 6, 8, 10, 12, 15, 16, 20, 24, 30, 36, 40, 48, 60,
] as const;
export const COLUMN_CHOICES = [1, 2, 3, 4, 5, 6] as const;

export type PageOrientation = "portrait" | "landscape";

/** `@page` size and margin per paper: 0.5 in on Letter, 12 mm on A4. */
const PAGE_RULES: Record<PaperSize, { size: string; margin: string }> = {
  letter: { size: "letter", margin: "0.5in" },
  a4: { size: "A4", margin: "12mm" },
};

export function clampProblemCount(value: number): number {
  if (!Number.isFinite(value)) {
    return MIN_PROBLEMS;
  }
  return Math.min(MAX_PROBLEMS, Math.max(MIN_PROBLEMS, Math.round(value)));
}

/**
 * How many columns a stacked or one-line problem needs.
 *
 * `width` is the longest problem in characters (digits, operator and spaces). Stacked
 * problems are narrow, so they pack more per row; one-line problems need room for the
 * whole equation. The result is a starting point the teacher can override.
 */
export function suggestedColumns(
  layout: ProblemLayout,
  width: number
): (typeof COLUMN_CHOICES)[number] {
  if (layout === "vertical") {
    if (width <= 6) {
      return 6;
    }
    if (width <= 8) {
      return 5;
    }
    if (width <= 10) {
      return 4;
    }
    return 3;
  }
  if (width <= 10) {
    return 4;
  }
  if (width <= 16) {
    return 3;
  }
  return 2;
}

/** Splits a list into rows of `size` (the last row may be short). */
export function chunk<T>(items: readonly T[], size: number): T[][] {
  const rows: T[][] = [];
  const step = Math.max(1, Math.floor(size));
  for (let i = 0; i < items.length; i += step) {
    rows.push(items.slice(i, i + step));
  }
  return rows;
}

/**
 * Print CSS for a worksheet.
 *
 * The browser's own margins are replaced by `@page`, the page column's width limit is
 * lifted, the preview card loses its border and shadow, and the answer key starts on a
 * new sheet. `.ws-avoid-break` keeps a problem from being split across two pages.
 */
export function worksheetPrintCss(
  paper: PaperSize,
  orientation: PageOrientation = "portrait"
): string {
  const page = PAGE_RULES[paper];
  return [
    `@page { size: ${page.size} ${orientation}; margin: ${page.margin}; }`,
    "@media print {",
    "  [data-tool-page] > div { max-width: none !important; }",
    "  .ws-sheet { border: 0 !important; border-radius: 0 !important; box-shadow: none !important; padding: 0 !important; margin: 0 !important; max-width: none !important; width: 100% !important; }",
    "  .ws-key { break-before: page; }",
    "  .ws-avoid-break { break-inside: avoid; }",
    "}",
  ].join("\n");
}
